import React, { Component } from "react";
import { Modal, message } from "antd";
import { CopyOutlined, ExportOutlined } from "@ant-design/icons";
import CabTrackView from "../../cabTrackView";
import { fetchCabTracking } from "../../cabTrackView/cabTracker";
import less from "./trackCab.module.scss";

const POLL_INTERVAL_MS = 30 * 1000;
// Polling stops once every cab is in one of these
const FINAL_STATES = ["COMPLETED", "INVALID"];

export const getCabTrackUrl = cabBookingGroupId =>
  `${window.location.origin}/cab-track/${cabBookingGroupId}`;

class TrackCabModal extends Component {
  state = {
    loading: true,
    cabs: []
  };

  componentDidMount() {
    this.fetchCabs();
    this.pollTimer = setInterval(this.fetchCabs, POLL_INTERVAL_MS);
  }

  componentWillUnmount() {
    clearInterval(this.pollTimer);
    this.unmounted = true;
  }

  fetchCabs = async () => {
    const { cabGroupIds } = this.props;
    if (!cabGroupIds || !cabGroupIds.length) {
      this.setState({ loading: false, cabs: [] });
      return;
    }
    const results = await Promise.all(
      cabGroupIds.map(id => fetchCabTracking(id))
    );
    if (this.unmounted) return;

    // A failed refresh keeps that cab's last data instead of dropping it
    const cabs = cabGroupIds.map((cabBookingGroupId, index) => {
      const { data, state } = results[index];
      const previous = this.state.cabs.find(
        cab => cab.cabBookingGroupId === cabBookingGroupId
      );
      if (data) {
        return {
          cabBookingGroupId,
          state: data.state,
          groupCode: data.groupCode,
          location: data.location,
          eta: data.eta
        };
      }
      if (previous && state !== "INVALID" && previous.state !== "ERROR") {
        return previous;
      }
      return { cabBookingGroupId, state, location: null, eta: null };
    });

    this.setState({ loading: false, cabs });
    if (cabs.every(cab => FINAL_STATES.includes(cab.state))) {
      clearInterval(this.pollTimer);
    }
  };

  // One state for the popup from each cab's own (backend) state: live if
  // any cab is, else the state the cabs share, else the most useful one
  getTrackingState = () => {
    const { loading, cabs } = this.state;
    if (loading) return "LOADING";

    const states = cabs.map(cab => cab.state);
    const has = state => states.includes(state);
    if (has("LIVE")) return "LIVE";
    if (states.length && states.every(state => state === states[0])) {
      return states[0];
    }
    if (has("NOT_STARTED")) return "NOT_STARTED";
    if (has("UNAVAILABLE")) return "UNAVAILABLE";
    if (has("OFFLINE")) return "OFFLINE";
    if (has("ERROR")) return "ERROR";
    return "COMPLETED";
  };

  copyLink = cabBookingGroupId => {
    navigator.clipboard
      .writeText(getCabTrackUrl(cabBookingGroupId))
      .then(() => message.success("Tracking link copied"))
      .catch(() => message.error("Unable to copy link"));
  };

  renderLinks = () => {
    const { cabGroupIds } = this.props;
    const { cabs } = this.state;

    return (
      <div className={less.links}>
        {cabGroupIds.map((cabBookingGroupId, index) => {
          const cab = cabs.find(c => c.cabBookingGroupId === cabBookingGroupId);
          const label =
            (cab && cab.location && cab.location.vehicleNumber) ||
            (cab && cab.groupCode) ||
            `Cab ${index + 1}`;
          return (
            <div key={cabBookingGroupId} className={less.linkRow}>
              <span>{label}</span>
              <a
                href={getCabTrackUrl(cabBookingGroupId)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExportOutlined /> Open page
              </a>
              <span
                className={less.copy}
                onClick={() => this.copyLink(cabBookingGroupId)}
              >
                <CopyOutlined /> Copy link
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  render() {
    const { onClose, cabGroupIds } = this.props;
    const trackedCabs = this.state.cabs.filter(
      cab => cab.state === "LIVE" && cab.location
    );
    const locations = trackedCabs.map(cab => cab.location);
    const trackingState = this.getTrackingState();
    // Attached cabs, not just the ones reporting: a second cab that hasn't
    // sent a ping yet still makes a single widget misleading
    const hasSingleCab = cabGroupIds.length === 1 && trackedCabs.length === 1;

    return (
      <Modal
        visible
        title={<span className={less.title}>Track Cab</span>}
        footer={null}
        onCancel={onClose}
        width={476}
        centered
        destroyOnClose
        className={less.modal}
      >
        <CabTrackView
          state={trackingState}
          locations={locations}
          eta={hasSingleCab ? trackedCabs[0].eta : null}
        />
        {this.renderLinks()}
      </Modal>
    );
  }
}

export default TrackCabModal;
